/**
 * Loading markup for Work port views. The seam renders this leaf while a lazy
 * slot loads, so it stays off the canvas cone and never reads a port.
 */
import { Skeleton } from '@heidi/design-system/skeleton';
import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

export const PreviewSkeletonBody = () => (
  <div className="flex flex-col gap-3 px-4 pt-6" aria-hidden>
    <Skeleton className="h-3 w-11/12" />
    <Skeleton className="h-3 w-full" />
    <Skeleton className="h-3 w-4/5" />
    <Skeleton className="h-3 w-2/3" />
  </div>
);

const PatientSkeletonBlock = ({ className }: { className?: string }) => (
  <div className={cn('h-3 animate-pulse rounded bg-surface-elevated', className)} />
);

export const PatientPanelSkeleton = () => (
  <div className="flex flex-col gap-3 p-5" data-testid="patient-artifact-skeleton">
    <PatientSkeletonBlock className="w-3/4" />
    <PatientSkeletonBlock className="w-1/2" />
    <PatientSkeletonBlock className="w-full" />
    <PatientSkeletonBlock className="w-2/3" />
  </div>
);

export const TemplateBuilderResolvingSkeleton = () => (
  <div
    className="flex flex-col gap-3 p-4"
    data-testid="template-builder-resolving-skeleton"
  >
    <Skeleton className="h-4 w-1/2" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-3/4" />
  </div>
);

export const CenteredSpinner = ({ size }: { size: 5 | 8 }) =>
  size === 8 ? (
    <div className="flex size-full items-center justify-center">
      <Loader2 className="size-8 animate-spin text-text-tertiary" />
    </div>
  ) : (
    <div
      className="flex h-full items-center justify-center"
      data-testid="work-explore-loading"
    >
      <Loader2 className="size-5 animate-spin text-text-tertiary" aria-hidden />
    </div>
  );

export const RoutineOverviewSkeleton = () => (
  <div className="flex flex-col gap-3 px-6 py-8" data-testid="routine-overview-skeleton">
    <Skeleton className="h-3 w-3/4" />
    <Skeleton className="h-3 w-1/2" />
    <Skeleton className="h-3 w-full" />
  </div>
);

export const LiveViewFrame = (props: { className?: string }) => (
  <div className={cn('aspect-video w-full bg-black', props.className)} />
);

export const ChunkErrorText = (props: { children?: ReactNode }) => (
  <TypographyP2>{props.children}</TypographyP2>
);
